import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const sectionSx = {
  mb: 2,
  borderRadius: 3,
  border: 1,
  borderColor: "divider",
  overflow: "hidden",
  "&:before": { display: "none" },
};

const summarySx = {
  px: { xs: 2, sm: 3 },
  minHeight: 64,
  "& .MuiAccordionSummary-content": { my: 1.5 },
};

const detailsSx = { px: { xs: 2, sm: 3 }, pt: 0, pb: { xs: 2, sm: 3 } };

const URDU_FONT_STACK = '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Al Qalam Quran Majeed", sans-serif';

function FeatureGroup({ title, items }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Stack component="ul" spacing={0.5} sx={{ pl: 3, m: 0, color: "text.secondary" }}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Stack>
    </Box>
  );
}

const EN_SINGLE_FEATURES = [
  "Automatic operator detection and strict column validation (Jazz, Zong, Ufone, Telenor)",
  "MFC — Most Frequent Contacts, with incoming/outgoing call and SMS counts",
  "MFL — Most Frequent Locations, with activity per cell site",
  "IMEI Summary and IMEI Scan — device usage and number-swap detection",
  "Short Codes and Other/Unresolved — no record is silently dropped from the analysis",
  "Insights — overview stats, contact-network scoring, and burst-activity windows",
  "Data Quality — missing fields, duplicate rows, and other export issues",
  "Network Graph — a visual contact/device map, sized and colored by call volume and direction",
  "Timeline — a swim-lane view of calls and SMS per contact, with burst windows highlighted",
  "Map — cell-tower locations plotted on an interactive, clustered map",
  "Filters (date range, IMEI, B-Party search) and export to Excel or PDF",
];

const EN_MULTIPLE_FEATURES = [
  "Upload 2 to 5 CDR files from different subjects",
  "Automatically finds shared IMEI (the same device used across different numbers)",
  "Finds shared contacts, and direct contact between two subjects' own numbers",
  "Finds shared locations across the uploaded files",
  "A visual graph connecting the files, plus a plain results table",
  "Export the findings to Excel",
];

const EN_UPTOFIVE_FEATURES = [
  "Same 2-to-5-file batch as Multiple CDR Analysis",
  "Focused on one consolidated report instead of an interactive graph",
  "Per-file summary table plus a cross-file findings table",
  "One-click consolidated Excel export",
];

const UR_SINGLE_FEATURES = [
  "آپریٹر کی خودکار شناخت اور کالمز کی سخت جانچ (جاز، زونگ، یوفون، ٹیلی نار)",
  "ایم ایف سی — سب سے زیادہ رابطے، آنے اور جانے والی کالز اور ایس ایم ایس کی تعداد کے ساتھ",
  "ایم ایف ایل — سب سے زیادہ استعمال ہونے والے مقامات، ہر سیل سائٹ کی سرگرمی کے ساتھ",
  "آئی ایم ای آئی کا خلاصہ اور اسکین — ڈیوائس کے استعمال اور نمبر تبدیل ہونے کی نشاندہی",
  "شارٹ کوڈز اور غیر واضح ریکارڈز — کوئی بھی ریکارڈ خاموشی سے نظرانداز نہیں کیا جاتا",
  "بصیرت — مجموعی اعداد و شمار، رابطوں کا اسکور، اور مصروف ترین اوقات کی نشاندہی",
  "ڈیٹا کوالٹی — غائب فیلڈز، دہرائے گئے ریکارڈز، اور دیگر مسائل کی جانچ",
  "نیٹ ورک گراف — رابطوں اور ڈیوائسز کا بصری نقشہ، کال کی مقدار اور رخ کے مطابق رنگ و حجم",
  "ٹائم لائن — ہر رابطے کی کالز اور ایس ایم ایس کا وقت کے ساتھ منظر، مصروف اوقات نمایاں کیے گئے",
  "نقشہ — سیل ٹاورز کے مقامات ایک انٹرایکٹو نقشے پر دکھائے جاتے ہیں",
  "فلٹرز (تاریخ، آئی ایم ای آئی، بی پارٹی تلاش) اور ایکسل یا پی ڈی ایف میں برآمد",
];

const UR_MULTIPLE_FEATURES = [
  "مختلف افراد کی 2 سے 5 تک سی ڈی آر فائلیں اپ لوڈ کریں",
  "مشترکہ آئی ایم ای آئی خودکار طور پر تلاش کرتا ہے (ایک ہی ڈیوائس مختلف نمبروں پر استعمال ہونا)",
  "مشترکہ رابطے، اور دو افراد کے اپنے نمبروں کے درمیان براہ راست رابطہ بھی تلاش کرتا ہے",
  "اپ لوڈ کی گئی فائلوں میں مشترکہ مقامات تلاش کرتا ہے",
  "فائلوں کو ملانے والا بصری گراف، اور ایک سادہ نتائج کی جدول",
  "نتائج کو ایکسل میں برآمد کریں",
];

const UR_UPTOFIVE_FEATURES = [
  "ایک سے پانچ فائلوں کا وہی بیچ جو ایک سے زیادہ سی ڈی آر تجزیے میں ہے",
  "بصری گراف کی بجائے ایک مجموعی رپورٹ پر توجہ",
  "ہر فائل کا خلاصہ جدول اور فائلوں کے درمیان مشترکہ نتائج کی جدول",
  "ایک کلک میں مجموعی ایکسل برآمد",
];

export default function CdrFeaturesGuide() {
  return (
    <Box sx={{ mb: 2 }}>
      <Accordion disableGutters elevation={0} sx={sectionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>What This Analysis Provides</Typography>
          <Chip size="small" label="English" variant="outlined" />
        </Stack>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>

        <FeatureGroup title="Single CDR Analysis" items={EN_SINGLE_FEATURES} />
        <FeatureGroup title="Multiple CDR Analysis" items={EN_MULTIPLE_FEATURES} />
        <FeatureGroup title="Up to 5 CDRs Analysis" items={EN_UPTOFIVE_FEATURES} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>How Multiple CDR Analysis Works</Typography>
        <Typography color="text.secondary" paragraph>
          Each uploaded file is parsed and analyzed on its own first, exactly like a single-file
          analysis. The tool then compares the results across files to look for four kinds of
          overlap: the same device (IMEI) appearing in more than one file, a contact number that
          appears in more than one subject's frequent-contact list, a case where one subject's own
          number appears as a contact in another subject's file (a direct contact between the two),
          and a location visited by more than one subject. Any of these can indicate that two
          subjects are connected — sharing a device, being in contact with each other, or having
          been in the same place — though none of them alone proves a relationship or an event.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>How to Use This Tool</Typography>
        <Stack component="ol" spacing={0.5} sx={{ pl: 3, m: 0, color: "text.secondary" }}>
          <li>Choose Single, Multiple, or Up to 5 CDRs analysis depending on how many files you have.</li>
          <li>Upload an authorized `.xlsx`, `.xls`, `.xlsm`, `.xlsb`, or `.csv` CDR export.</li>
          <li>Review the detected operator, exact headers, and any data-quality warnings.</li>
          <li>Use the date range, IMEI, and B-Party filters to narrow the results to what matters.</li>
          <li>Export the filtered results to Excel or PDF, which include the filters used as metadata.</li>
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 1.5, fontStyle: "italic" }}>
          Note: CDR analysis produces evidence-oriented summaries and investigative leads, not proof
          by itself. Always verify results against the original records, lawful authorization, and
          independent evidence before drawing any conclusion.
        </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ ...sectionSx, direction: "rtl", fontFamily: URDU_FONT_STACK }} lang="ur">
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip size="small" label="اردو" variant="outlined" />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800, fontFamily: URDU_FONT_STACK }}>
            یہ تجزیہ کیا فراہم کرتا ہے
          </Typography>
        </Stack>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontFamily: URDU_FONT_STACK }}>سنگل سی ڈی آر تجزیہ</Typography>
          <Stack component="ul" spacing={0.5} sx={{ pr: 3, pl: 0, m: 0, color: "text.secondary", fontFamily: URDU_FONT_STACK }}>
            {UR_SINGLE_FEATURES.map((item) => <li key={item}>{item}</li>)}
          </Stack>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontFamily: URDU_FONT_STACK }}>ایک سے زیادہ سی ڈی آر تجزیہ</Typography>
          <Stack component="ul" spacing={0.5} sx={{ pr: 3, pl: 0, m: 0, color: "text.secondary", fontFamily: URDU_FONT_STACK }}>
            {UR_MULTIPLE_FEATURES.map((item) => <li key={item}>{item}</li>)}
          </Stack>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontFamily: URDU_FONT_STACK }}>پانچ تک سی ڈی آر تجزیہ</Typography>
          <Stack component="ul" spacing={0.5} sx={{ pr: 3, pl: 0, m: 0, color: "text.secondary", fontFamily: URDU_FONT_STACK }}>
            {UR_UPTOFIVE_FEATURES.map((item) => <li key={item}>{item}</li>)}
          </Stack>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5, fontFamily: URDU_FONT_STACK }}>
          ایک سے زیادہ سی ڈی آر تجزیہ کیسے کام کرتا ہے
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ fontFamily: URDU_FONT_STACK }}>
          ہر اپ لوڈ کی گئی فائل کو پہلے علیحدہ طور پر پرکھا اور تجزیہ کیا جاتا ہے، بالکل اسی طرح
          جیسے سنگل فائل کا تجزیہ ہوتا ہے۔ اس کے بعد یہ ٹول تمام فائلوں کے نتائج کا موازنہ کر کے
          چار قسم کی مشترکات تلاش کرتا ہے: ایک ہی ڈیوائس (آئی ایم ای آئی) کا ایک سے زیادہ فائلوں
          میں ظاہر ہونا، ایک ایسا رابطہ نمبر جو ایک سے زیادہ افراد کی فہرست میں موجود ہو، ایسی
          صورتحال جہاں ایک شخص کا اپنا نمبر دوسرے شخص کی فائل میں بطور رابطہ ظاہر ہو (یعنی دونوں
          کے درمیان براہ راست رابطہ)، اور کوئی ایسا مقام جہاں ایک سے زیادہ افراد موجود رہے ہوں۔ ان
          میں سے کوئی بھی نشانی دو افراد کے درمیان تعلق کی نشاندہی کر سکتی ہے — چاہے وہ ڈیوائس کا
          اشتراک ہو، آپس میں رابطہ ہو، یا ایک ہی جگہ موجودگی — لیکن اکیلے کوئی بھی نشانی حتمی ثبوت
          نہیں ہے۔
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5, fontFamily: URDU_FONT_STACK }}>
          یہ ٹول کیسے استعمال کریں
        </Typography>
        <Stack component="ol" spacing={0.5} sx={{ pr: 3, pl: 0, m: 0, color: "text.secondary", fontFamily: URDU_FONT_STACK }}>
          <li>اپنی فائلوں کی تعداد کے مطابق سنگل، ایک سے زیادہ، یا پانچ تک سی ڈی آر تجزیہ منتخب کریں۔</li>
          <li>مجاز `.xlsx`, `.xls`, `.xlsm`, `.xlsb`, یا `.csv` سی ڈی آر فائل اپ لوڈ کریں۔</li>
          <li>شناخت شدہ آپریٹر، کالمز، اور ڈیٹا کوالٹی کی وارننگز کا جائزہ لیں۔</li>
          <li>تاریخ، آئی ایم ای آئی، اور بی پارٹی فلٹرز کے ذریعے نتائج کو محدود کریں۔</li>
          <li>فلٹر شدہ نتائج کو ایکسل یا پی ڈی ایف میں برآمد کریں، جس میں استعمال شدہ فلٹرز بھی شامل ہوں گے۔</li>
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 1.5, fontStyle: "italic", fontFamily: URDU_FONT_STACK }}>
          نوٹ: سی ڈی آر تجزیہ تحقیقاتی رہنمائی اور شواہد پر مبنی خلاصہ فراہم کرتا ہے، مگر یہ خود
          بذاتِ خود حتمی ثبوت نہیں ہے۔ کوئی بھی نتیجہ اخذ کرنے سے پہلے اصل ریکارڈز، قانونی اجازت،
          اور آزادانہ شواہد سے تصدیق ضرور کریں۔
        </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
